import { AnalyticsComponentName } from '@app/pages/i/analytics';
import { Text } from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { ModalSize, useOpenModal } from '@redbubble/next/modals';
import type { UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import { uniqBy } from 'lodash';
import React, { memo, useEffect, useMemo } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { useListsInclusions } from '../../../../hooks/useListsInclusions';
import {
  DefaultInventoryItem,
  InventoryItem,
  Work,
} from '../../../../types/shop-bff/types';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import {
  parseDefaultInventoryItemAndWorkToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '../../analytics';
import { PreviewStyle } from '../../constants';
import { AlsoAvailableOnModal } from '../AlsoAvailableOnModal';
import { ArtworkInfo } from '../ArtworkInfo';
import { AlsoAvailableOnModalButton } from './AlsoAvailableOnModalButton';
import { AlsoAvailableOnSectionFooter } from './AlsoAvailableOnSectionFooter';
import { AlsoAvailableOnSectionHeader } from './AlsoAvailableOnSectionHeader';
import {
  groupSwitchableAndUnswitchableProducts,
  mapDefaultInventoryItemToMediaCardProps,
  mapPriceProps,
  useOtherProducts,
} from './utils';

export const AAO_SEO_LINK_LIMIT = 4;

export interface AlsoAvailableOnCarouselProps {
  availableProducts: DefaultInventoryItem[];
  work: Work;
  defaultItemId: string;
  experiences: InventoryItem['experiences'];
  gaCode: InventoryItem['gaCode'];
  productTypeId: InventoryItem['productTypeId'];
  workId: InventoryItem['workId'];
}

export const messages = defineMessages({
  alsoAvailableOn: {
    defaultMessage: 'Also available on',
  },
  viewThisDesignOn: {
    defaultMessage: `View this design on {numberOfAvailableProducts, plural,
        one {+{numberOfAvailableProducts} product}
        other {+{numberOfAvailableProducts} products}}
      `,
  },
});

export const AlsoAvailableOnSection: React.FC<AlsoAvailableOnCarouselProps> =
  memo(
    ({
      availableProducts,
      work,
      defaultItemId,
      experiences,
      gaCode,
      productTypeId,
      workId,
    }) => {
      const intl = useIntl();
      const openModal = useOpenModal();
      const [{ locale, country, currency }] = useUserInfo<UserInfo>();

      const {
        otherProducts,
        loading: otherProductsLoading,
        refresh: refreshOtherProducts,
      } = useOtherProducts();

      // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
      useEffect(() => {
        refreshOtherProducts({
          inventoryItemId: defaultItemId,
          currency,
          country,
          locale,
          previewTypeIds: [PreviewStyle.DEFAULT],
        });
      }, [country, currency, locale, refreshOtherProducts]);

      const additionalProducts = otherProducts;

      const [switchableProducts, unswitchableProducts] =
        groupSwitchableAndUnswitchableProducts(
          productTypeId,
          availableProducts
        );
      const sortedAvailableProducts = [
        ...switchableProducts,
        ...unswitchableProducts,
      ];

      const items: DefaultInventoryItem[] = (
        additionalProducts
          ? uniqBy(
              [...additionalProducts, ...sortedAvailableProducts],
              'productTypeId'
            )
          : sortedAvailableProducts
      )
        .filter((item) => item.productTypeId !== productTypeId)
        .slice(0, 20);

      const { listsInclusions, refresh: refreshListInclusions } =
        useListsInclusions();

      useEffect(() => {
        if (!otherProductsLoading) refreshListInclusions(items);
      }, [items, otherProductsLoading, refreshListInclusions]);

      const handleViewMoreDesignClick = () => {
        openModal({
          accessibleTitle: intl.formatMessage(messages.alsoAvailableOn),
          content: (
            <ErrorBoundary>
              <AlsoAvailableOnModal
                items={availableProducts}
                matureContent={work.matureContent}
                artistName={work.artistUsername}
              />
            </ErrorBoundary>
          ),
          size: ModalSize.Large,
        });
      };

      // minus 1 because the current product is included in the count
      const numberOfAvailableProducts = availableProducts.length - 1;

      // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
      const contextValue: CarouselSectionContextValue = useMemo(() => {
        return {
          mediaCardPropsMapper: mapDefaultInventoryItemToMediaCardProps(
            work,
            listsInclusions,
            AAO_SEO_LINK_LIMIT
          ),
          pricePropsMapper: mapPriceProps,
          analytics: {
            componentName: AnalyticsComponentName.SimilarProducts,
            listID: AnalyticsItemsList.AlsoAvailableOnCarousel,
            inViewTracking: () => {
              const analyticsItems = items
                .map((inventoryItem, index) => {
                  if (!inventoryItem) return null;

                  return parseDefaultInventoryItemAndWorkToAnalyticsEcommerceItem(
                    {
                      inventoryItem,
                      work,
                      index,
                    }
                  );
                })
                .filter(
                  (
                    item: AnalyticsEcommerceItem | null
                  ): item is NonNullable<AnalyticsEcommerceItem> => !!item
                );

              trackGA4ViewItemListEvent({
                items: analyticsItems,
                listID: AnalyticsItemsList.AlsoAvailableOnCarousel,
                topic: '',
                searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
              });
            },
          },
          rsLocation: RSProductClickedLocation.AlsoAvailableOnCarousel,
          rsPageType: RSPageType.Product,
        };
      }, [items, listsInclusions]);

      return (
        <CarouselSectionContext.Provider value={contextValue}>
          <CarouselSection.Container>
            <ArtworkInfo work={work} />
            <AlsoAvailableOnSectionHeader>
              <Text type="display3" display="block" element="h2">
                {intl.formatMessage(messages.alsoAvailableOn)}
              </Text>
              <AlsoAvailableOnModalButton
                handleClick={handleViewMoreDesignClick}
              >
                {intl.formatMessage(messages.viewThisDesignOn, {
                  numberOfAvailableProducts,
                })}
              </AlsoAvailableOnModalButton>
            </AlsoAvailableOnSectionHeader>
            <CarouselSection.Carousel>
              {items.map((item, index) => {
                return (
                  <CarouselSection.CarouselSlide key={`aao-slide-${item?.id}`}>
                    <CarouselSection.CarouselMediaCard
                      data={item}
                      isLast={index === items.length - 1}
                      index={index}
                    />
                  </CarouselSection.CarouselSlide>
                );
              })}
            </CarouselSection.Carousel>
            <AlsoAvailableOnSectionFooter>
              <AlsoAvailableOnModalButton
                handleClick={handleViewMoreDesignClick}
              >
                {intl.formatMessage(messages.viewThisDesignOn, {
                  numberOfAvailableProducts,
                })}
              </AlsoAvailableOnModalButton>
            </AlsoAvailableOnSectionFooter>
          </CarouselSection.Container>
        </CarouselSectionContext.Provider>
      );
    }
  );
